<script lang="ts">
    
    import { onDestroy, onMount, setContext } from "svelte";
    import { writable } from "svelte/store";
    import { Icon, Modal, ModalBody, ModalHeader } from "sveltestrap";
    import RoomSettings from "../../Components/Manager/RoomSettings.svelte";
    import MobileWrapper from "../../Components/Shared/Mobile/MobileWrapper.svelte";
    import type { ManagerState } from "../../global";
    import type { Game } from "../../Model/Game";
    import { SubscriptionsManager } from "../../Model/SubscriptionsManager";
    import { gamesService, meService, realTimeService } from "../../Services";
    import { i18n } from "../../Services/i18n";
    import Idle from "./Idle.svelte";
    import Playing from "./Playing.svelte";
    import Waiting from "./Waiting.svelte";
    import type { User } from "../../Model/User";
    import SignalIcon from "../../Components/Shared/Icons/SignalIcon.svelte";

    const managerStateComponentsMap = {
        idle: Idle,
        waiting: Waiting,
        playing: Playing,
    };
    
    const gameSubscriptionsManager = new SubscriptionsManager();
    const gameStore = writable<Game | null>(null);

    let game: Game | null = null;
    let managerState: ManagerState | 'loading' | undefined | 'error' = 'loading';
    let openRoomSettingsModal = false;
    let me: User = null;
    let signal: string = '~';

    $: meService.me.subscribe((value) => me = value)
    $: realTimeService.signal.subscribe((value) => signal = value)
    setContext('setGame', setGame);
    setContext('getGame', getGame);
    setContext('gameStore', gameStore);
    setContext('clearGame', clearGame);
    setContext('toggleRoomSettingsModal', toggleRoomSettingsModal);

    let retries = 0;
    let codeError = null;

    onMount(async () => {
        await donwloadGame();
    });

    async function connectToSocket(roomId) {
        meService.me.subscribe(user => {
            if (user) {
                realTimeService.start(user.id, roomId);
            } else {
                realTimeService.end();
            }
        });
    }

    async function init() {
        if (retries < 2) {
            retries++;

            window.addEventListener('offline', () => {
                // isOnline = false
                realTimeService.signal.set('/');
                realTimeService.send('lost_network', {
                    game,
                    user_id: me.id
                })
            });
            window.addEventListener('online', () => {});
        } else {
            managerState = 'error';
            codeError = 'e-1001'
        }
    }

    async function donwloadGame() {
        try {
            const game = await gamesService.getGameInCourseForTheCurrentManager();
            if (game) {
                connectToSocket(game.roomId);
                init()
                setGame(game);
            } else {
                clearGame();
            }
        } catch(error) {
            console.log(error);
            // await init();
        }
    }

    onDestroy(() => {
        realTimeService.end();
    });

    function toggleRoomSettingsModal(): void {
        openRoomSettingsModal = !openRoomSettingsModal;
    }

    function setGame(_game: Game): void {
        game = _game;
        gameSubscriptionsManager.unsubscribeAll();
        gameSubscriptionsManager.subscribe(game.state, gameState => {
            if (gameState === 'completed') {
                return;
            }
            managerState = gameState;
        });
        $gameStore = _game;
    }

    function getGame(): Game | null {
        return game;
    }

    function clearGame(): void {
        // if ($gameStore && $gameStore.roomId !== null) {
        //     console.log($gameStore.roomId);
        //     realTimeService.send('game_cancel', { roomId: $gameStore.roomId });
        // }
        // console.log('clearGame', roomId);

        game = null;
        gameSubscriptionsManager.unsubscribeAll();
        $gameStore = null;
        managerState = 'idle';
    }

</script>

<MobileWrapper>

    {#if !managerState}
        <div class="view">
            {$i18n("Cargando")}
        </div>
    {:else}
        {#key managerState}
            <div class="view">
                <svelte:component this={managerStateComponentsMap[managerState]} />
            </div>
            {#if managerState == 'error'}
                <div class="position-container">
                    <div class="centered-div">
                        <div style='display:flex; gap: 10px;align-items: center;color:white;'>
                            <span style='font-size: 20px'><Icon name="exclamation-triangle-fill" /></span>
                            <span>
                                {$i18n('Se ha generado un error')}
                            </span>
                        </div>
                        <p style='margin-bottom: 10px;color:white;font-size: 12px;'>
                            Error code: {codeError}
                        </p>
                        <button on:click={donwloadGame} class='btn btn-warning'>Reintentar</button>
                    </div>
                </div>
            {/if}
            <div class='helpLink'>
                <span style="color: white;">
                    <span class='me-1'><SignalIcon size={20} bind:signal /></span>
                    v4.4.0
                    — 
                    <a class='linkText' target='_blank' href="https://funboxmusic.com/incidents-in-the-application/">{$i18n("Comunicar incidencia")}</a>
                </span>
            </div>
        {/key}
    {/if}

</MobileWrapper>

{#if game}
    <Modal isOpen={openRoomSettingsModal}>
        <ModalHeader toggle={toggleRoomSettingsModal}>{$i18n("Ajustes de la sala")}</ModalHeader>
        <ModalBody>
            <RoomSettings roomId={game.roomId} on:saved={toggleRoomSettingsModal} />
        </ModalBody>
    </Modal>
{/if}



<style lang="scss">

    :root {
        --color-primary: #004681;
        --color-secondary: #FFD633;
        --color-invalid: rgb(238, 58, 17);
        --color-success: rgb(68, 182, 91);
    }

    .view {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        background-image: url('/images/mobile-background.svg');
        background-position: center;
        background-repeat: no-repeat;
        background-size: cover;
        color: #fff;
    }

    .helpLink {
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        text-align: center;
        display: flex;
        justify-self: center;
        align-items: end;
        padding: 10px;
    }
    .linkText {
        color: white
    }
    .linkText:hover {
        color: var(--color-secondary)
    }

    .position-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.5); /* Semi-transparent backdrop */
  z-index: 10;
}

/* Centered div */
.centered-div {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  display: flex;
  flex-direction: column;
  background-color: #dc3545;
  padding: 20px;
  border-radius: 15px;
  width: 65%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px
}

</style>
